import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@libs/shared/auth';
import { AppStateService } from '@libs/shared/common';
import {
  buildNavigationTree,
  CHECKOUT_CART_ROUTE_NAME,
  NAVBAR_OFFERS_ROUTE_NAME,
  NavItemModel,
  NavItemNodeModel,
  NavModel,
  USER_HOME_ROUTE_NAME,
} from '@libs/shared/models';
import { combineLatest, defer, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { CapabilityService } from '../capability';
import { CmsService } from '../cms';
import { ViewportService } from '../viewport';
import { NavigationConfigRepositoryService, NavigationRepositoryService } from './repository';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly state: AppStateService = inject(AppStateService);
  private readonly injector: Injector = inject(Injector);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly cmsService: CmsService = inject(CmsService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly router: Router = inject(Router);
  private readonly viewportService: ViewportService = inject(ViewportService);

  private navigationRepository?: NavigationRepositoryService;

  private readonly homeNav$: Observable<NavModel> = this.initHomeNavigation();
  private readonly mainNav$: Observable<NavModel> = this.initMainNavigation();
  private readonly userNav$: Observable<NavModel> = this.initUserNavigation();
  private readonly agentNav$: Observable<NavModel> = this.initAgentNavigation();
  private readonly footerNav$: Observable<NavModel> = this.initFooterNavigation();
  private readonly catalogNav$: Observable<NavModel> = this.initCatalogNavigation();
  private readonly checkoutNav$: Observable<NavModel> = this.initCheckoutNavigation();
  private readonly ordersNav$: Observable<NavModel> = this.initOrdersNavigation();
  private readonly appNav$: Observable<NavModel> = this.initAppNavigation();
  private readonly pageNav$: Observable<NavModel> = this.initPageNavigation();
  private readonly navbarNav$: Observable<NavModel> = this.initNavbarNavigation();

  public getMainNavigation(): Observable<NavModel> {
    return this.mainNav$;
  }

  public getUserNavigation(): Observable<NavModel> {
    return this.userNav$;
  }

  public getOrdersNavigation(): Observable<NavModel> {
    return this.ordersNav$;
  }

  public getPageNavigation(): Observable<NavModel> {
    return this.pageNav$;
  }

  public getAppNavigation(): Observable<NavModel> {
    return this.appNav$;
  }

  public getNavbarNavigation(): Observable<NavModel> {
    return this.navbarNav$;
  }

  public async closeOverlayRoute(): Promise<boolean | void> {
    const url: string | undefined = await firstValueFrom(this.state.get('lastContentUrl'));

    return this.router.navigateByUrl(String(url));
  }

  private initHomeNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getHomeNavigation(customer)),
      map(homeNav => this.buildNavigation(homeNav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initMainNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getMainNavigation(customer)),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initAgentNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(() => this.getRepository().getAgentNavigation()),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initUserNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getUserNavigation(customer)),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initFooterNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getFooterNavigation(customer)),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initCatalogNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getCatalogNavigation(customer)),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initCheckoutNavigation(): Observable<NavModel> {
    return defer(() => this.authService.getCurrentCustomer().pipe(
      switchMap(customer => this.getRepository().getCheckoutNavigation(customer)),
      map(nav => this.buildNavigation(nav)),
    )).pipe(
      shareReplay(1),
    );
  }

  private initOrdersNavigation(): Observable<NavModel> {

    return defer(() =>
      combineLatest({
        isMobile: this.viewportService.isMobile(),
        customer: this.authService.getCurrentCustomer(),
      }).pipe(
        switchMap(({ isMobile, customer }) => this.getRepository().getOrdersNavigation(isMobile, customer)),
        map(nav => this.buildNavigation(nav)),
      )).pipe(
      shareReplay(1),
    );
  }

  private initAppNavigation(): Observable<NavModel> {
    return defer(() => combineLatest([
      this.homeNav$,
      this.userNav$,
      this.agentNav$,
      this.footerNav$,
      this.catalogNav$,
      this.checkoutNav$,
      this.ordersNav$,
    ]).pipe(
      map(navs => ({
        children: navs.flatMap(nav => nav.children ?? []),
      })),
    )).pipe(
      shareReplay(1),
    );
  }

  private initPageNavigation(): Observable<NavModel> {
    return defer(() => this.state.get('page').pipe(
      switchMap(pageState => {
        if (!pageState) {
          return of({});
        }

        if (pageState.useAppMainNavigation) {
          return this.getMainNavigation();
        }

        return this.cmsService.getStructure(pageState.rootId, pageState.customerNumber).pipe(
          map(nav => this.buildNavigation(nav)),
        );
      }),
    )).pipe(
      shareReplay(1),
    );
  }

  private initNavbarNavigation(): Observable<NavModel> {
    return defer(() => {
      if (this.capabilityService.isAvailable('platform.cmsAppConfigMode')()) {
        return this.getMainNavigation().pipe(
          map((nav: NavModel) => ({
            children: (nav.children ?? []).slice(0, 5),
          })),
        );
      }

      const navbarBaseNav$: Observable<NavItemModel> = this.authService.getCurrentCustomer().pipe(
        switchMap(customer => this.getRepository().getNavbarNavigationBase(customer)),
        map(navbarNav => this.buildNavigation(navbarNav)),
      );

      // Combine with home and cart items
      return combineLatest([navbarBaseNav$, this.homeNav$, this.checkoutNav$, this.mainNav$]).pipe(
        map(([navbarBaseNav, homeNav, checkoutNav, mainNav]) => {
          const homeItem: NavItemModel | undefined = homeNav.children?.find(item => item.name === USER_HOME_ROUTE_NAME);
          const cartItem: NavItemModel | undefined = checkoutNav.children?.find(item => item.name === CHECKOUT_CART_ROUTE_NAME);
          const cmsContentAvailable: boolean = mainNav.children?.[0]?.type === 'content';
          let navbarItems: NavItemModel[] = navbarBaseNav.children ?? [];

          if (!cmsContentAvailable) {
            navbarItems = navbarItems.filter(
              item => item.name !== NAVBAR_OFFERS_ROUTE_NAME,
            );
          }

          const items: (NavItemModel | undefined)[] = [homeItem, ...navbarItems, cartItem];

          return { children: items.filter(Boolean) as NavItemModel[] };
        }),
      );
    }).pipe(
      shareReplay(1),
    );
  }

  private getRepository(): NavigationRepositoryService {
    this.navigationRepository ??= this.injector.get(
      this.capabilityService.isAvailable('platform.cmsAppConfigMode')() ? NavigationConfigRepositoryService : NavigationRepositoryService,
    );

    return this.navigationRepository;
  }

  private buildNavigation(rootNode: NavItemModel): NavModel {
    const result: NavItemNodeModel = buildNavigationTree(rootNode);

    result.label = 'Startseite';
    result.url = '/';

    return result;
  }
}
